import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { AppContext } from '../App';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import PageSeo from '../components/PageSeo';
import { SEO } from '../utils/seoConfig';
import { 
  FaSearch, FaStore, FaMapMarkerAlt, FaBox, FaStar, 
  FaPhone, FaEnvelope, FaGlobe, FaCheck, FaCrown, FaAward, FaShieldAlt
} from 'react-icons/fa';

export default function Suppliers() {
  const { i18n } = useTranslation();
  const { API } = useContext(AppContext);
  const [shops, setShops] = useState([]);
  const [filteredShops, setFilteredShops] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, premium, verified

  useEffect(() => {
    fetchShops();
  }, []);

  useEffect(() => {
    filterShops();
  }, [search, shops, filter]);

  const fetchShops = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/shops?include_subscription=true`);
      // Filter only active/verified shops
      const activeShops = response.data.filter(shop => shop.is_verified !== false);
      setShops(activeShops);
      setFilteredShops(activeShops);
    } catch (error) {
      console.error('Error fetching shops:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterShops = () => {
    let filtered = [...shops];
    
    // Filter by premium/verified
    if (filter === 'premium') {
      filtered = filtered.filter(shop => shop.owner_subscription?.is_premium);
    } else if (filter === 'verified') {
      filtered = filtered.filter(shop => shop.is_verified);
    }
    
    // Filter by search
    if (search.trim()) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(shop => 
        shop.name?.toLowerCase().includes(searchLower) ||
        shop.description?.toLowerCase().includes(searchLower) ||
        shop.city?.toLowerCase().includes(searchLower) ||
        shop.country?.toLowerCase().includes(searchLower) ||
        shop.owner_info?.company?.toLowerCase().includes(searchLower)
      );
    }
    
    // Sort: Premium first, then by position
    filtered.sort((a, b) => {
      const aPremium = a.owner_subscription?.is_premium ? 1 : 0;
      const bPremium = b.owner_subscription?.is_premium ? 1 : 0;
      if (bPremium !== aPremium) return bPremium - aPremium;
      return (a.position || 999) - (b.position || 999);
    });
    
    setFilteredShops(filtered);
  };

  const getShopImage = (shop) => {
    // Use logo if available
    if (shop.logo) {
      if (shop.logo.startsWith('http')) return shop.logo;
      if (shop.logo.startsWith('/api')) return `${API.replace('/api', '')}${shop.logo}`;
      return `${API}${shop.logo}`;
    }
    if (shop.logo_url) return shop.logo_url;
    if (shop.banner_url) return shop.banner_url;
    return null; // Return null to show fallback icon
  };

  const getShopLogo = (shop) => {
    if (shop.logo) {
      if (shop.logo.startsWith('http')) return shop.logo;
      if (shop.logo.startsWith('/api')) return `${API.replace('/api', '')}${shop.logo}`;
      return `${API}${shop.logo}`;
    }
    return null;
  };

  const getPlanBadge = (subscription) => {
    if (!subscription || !subscription.is_premium) return null;
    
    const planConfig = {
      sur_mesure: { 
        label: 'Premium+', 
        icon: FaCrown, 
        className: 'bg-gradient-to-r from-amber-500 to-yellow-400 text-white shadow-lg',
        glow: 'shadow-amber-300/50'
      },
      fournisseur: { 
        label: 'Fournisseur Pro', 
        icon: FaAward, 
        className: 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg',
        glow: 'shadow-purple-300/50'
      },
      pro: { 
        label: 'Pro', 
        icon: FaShieldAlt, 
        className: 'bg-gradient-to-r from-blue-600 to-cyan-500 text-white shadow-lg',
        glow: 'shadow-blue-300/50'
      }
    };
    
    const config = planConfig[subscription.plan] || planConfig.pro;
    const Icon = config.icon;
    
    return (
      <Badge className={`${config.className} ${config.glow} flex items-center gap-1 px-3 py-1 text-xs font-bold`}>
        <Icon className="text-sm" />
        {config.label}
      </Badge>
    );
  };

  const premiumCount = shops.filter(s => s.owner_subscription?.is_premium).length;
  const verifiedCount = shops.filter(s => s.is_verified).length;

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <PageSeo {...SEO.suppliers} />
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#0A2540] to-[#006D8F] text-white py-12">
        <div className="container mx-auto px-4">
          <h1 className="text-3xl md:text-4xl font-bold mb-4 text-center">
            {i18n.language === 'fr' ? '🏭 Nos Fournisseurs' : '🏭 Our Suppliers'}
          </h1>
          <p className="text-center text-white/80 max-w-2xl mx-auto mb-8">
            {i18n.language === 'fr' 
              ? 'Découvrez tous nos fournisseurs partenaires et leurs boutiques'
              : 'Discover all our partner suppliers and their shops'}
          </p>
          
          {/* Search Bar */}
          <div className="max-w-xl mx-auto">
            <div className="relative">
              <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <Input
                type="text"
                placeholder={i18n.language === 'fr' ? 'Rechercher un fournisseur...' : 'Search a supplier...'}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-12 py-6 text-lg rounded-full bg-white text-gray-800"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Filters & Stats */}
      <div className="container mx-auto px-4 py-6">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          {/* Filter Buttons */}
          <div className="flex flex-wrap gap-2">
            <Button 
              variant={filter === 'all' ? 'default' : 'outline'}
              onClick={() => setFilter('all')}
              className={filter === 'all' ? 'bg-[#0A2540]' : ''}
            >
              {i18n.language === 'fr' ? 'Tous' : 'All'} ({shops.length})
            </Button>
            <Button 
              variant={filter === 'premium' ? 'default' : 'outline'}
              onClick={() => setFilter('premium')}
              className={filter === 'premium' ? 'bg-gradient-to-r from-amber-500 to-yellow-400 border-0' : 'border-amber-400 text-amber-600 hover:bg-amber-50'}
            >
              <FaCrown className="mr-2" />
              Premium ({premiumCount})
            </Button>
            <Button 
              variant={filter === 'verified' ? 'default' : 'outline'}
              onClick={() => setFilter('verified')}
              className={filter === 'verified' ? 'bg-[#635BFF]' : ''}
            >
              <FaCheck className="mr-2" />
              {i18n.language === 'fr' ? 'Vérifiés' : 'Verified'} ({verifiedCount})
            </Button>
          </div>
          
          {/* Results count */}
          <p className="text-gray-600">
            {filteredShops.length} {i18n.language === 'fr' ? 'fournisseur(s) trouvé(s)' : 'supplier(s) found'}
          </p>
        </div>

        {/* Suppliers Grid */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0A2540]"></div>
          </div>
        ) : filteredShops.length === 0 ? (
          <div className="text-center py-20">
            <FaStore className="mx-auto text-6xl text-gray-300 mb-4" />
            <p className="text-gray-500 text-lg">
              {i18n.language === 'fr' 
                ? 'Aucun fournisseur trouvé'
                : 'No suppliers found'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredShops.map((shop) => {
              const isPremium = shop.owner_subscription?.is_premium;
              const shopLogo = getShopLogo(shop);
              
              return (
                <Link key={shop.id} to={`/shop?shop=${shop.id}`}>
                  <Card className={`hover:shadow-xl transition-all duration-300 overflow-hidden h-full group relative ${
                    isPremium ? 'ring-2 ring-amber-400/50 shadow-lg' : ''
                  }`}>
                    {/* Premium Glow Effect */}
                    {isPremium && (
                      <div className="absolute inset-0 bg-gradient-to-br from-amber-100/20 to-transparent pointer-events-none" />
                    )}
                    
                    {/* Header with Logo and Info */}
                    <div className={`relative p-5 ${isPremium ? 'bg-gradient-to-r from-amber-50 to-white' : 'bg-gradient-to-r from-gray-50 to-white'}`}>
                      {/* Premium Corner Ribbon */}
                      {isPremium && (
                        <div className="absolute top-0 left-0 w-20 h-20 overflow-hidden">
                          <div className="absolute top-2 left-[-30px] w-[120px] text-center text-[9px] font-bold text-white bg-gradient-to-r from-amber-500 to-yellow-400 transform -rotate-45 shadow-lg py-1">
                            ⭐ PREMIUM
                          </div>
                        </div>
                      )}
                      
                      <div className="flex items-start gap-4">
                        {/* Logo */}
                        <div className={`w-20 h-20 rounded-xl flex-shrink-0 flex items-center justify-center overflow-hidden shadow-md ${
                          shopLogo ? 'bg-white' : isPremium ? 'bg-gradient-to-br from-amber-100 to-amber-50' : 'bg-gradient-to-br from-[#635BFF]/10 to-[#00D4FF]/10'
                        }`}>
                          {shopLogo ? (
                            <img 
                              src={shopLogo} 
                              alt={shop.name}
                              className="w-full h-full object-contain p-1"
                            />
                          ) : (
                            <FaStore className={`text-3xl ${isPremium ? 'text-amber-500' : 'text-[#635BFF]'}`} />
                          )}
                        </div>
                        
                        {/* Name and Badge */}
                        <div className="flex-1 min-w-0 pt-1">
                          <div className="flex items-start justify-between gap-2">
                            <h3 className={`text-lg font-bold group-hover:text-[#00D4FF] transition-colors line-clamp-1 ${
                              isPremium ? 'text-[#0A2540]' : 'text-[#0A2540]'
                            }`}>
                              {shop.name}
                            </h3>
                            {shop.rating && (
                              <div className="flex items-center gap-1 text-yellow-500 flex-shrink-0">
                                <FaStar />
                                <span className="text-sm font-medium">{shop.rating.toFixed(1)}</span>
                              </div>
                            )}
                          </div>
                          
                          {/* Subtitle/Slogan - small text */}
                          {shop.subtitle && (
                            <p className="text-xs text-[#635BFF] font-medium mt-1 line-clamp-1 italic">
                              {shop.subtitle}
                            </p>
                          )}
                          
                          {/* Premium Badge */}
                          <div className="mt-2">
                            {getPlanBadge(shop.owner_subscription)}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <CardContent className="p-5 pt-3 relative">
                      {/* Description - Priority display */}
                      {(shop.description_fr || shop.description) && (
                        <p className="text-gray-700 text-sm mb-3 line-clamp-3">
                          {i18n.language === 'fr' ? (shop.description_fr || shop.description) : (shop.description_en || shop.description_fr || shop.description)}
                        </p>
                      )}
                      
                      {/* Company Name (if different) */}
                      {shop.owner_info?.company && shop.owner_info.company !== shop.name && (
                        <p className="text-sm text-gray-500 mb-2 flex items-center gap-1">
                          <FaStore className="text-xs" />
                          {shop.owner_info.company}
                        </p>
                      )}
                      
                      {/* Location */}
                      {(shop.city || shop.country) && (
                        <div className="flex items-center gap-2 text-gray-500 text-sm mb-3">
                          <FaMapMarkerAlt className="text-[#00D4FF]" />
                          <span>{[shop.city, shop.country].filter(Boolean).join(', ')}</span>
                        </div>
                      )}
                      
                      {/* Stats */}
                      <div className="flex items-center justify-between pt-3 border-t">
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <FaBox className="text-[#635BFF]" />
                          <span>{shop.product_count || 0} {i18n.language === 'fr' ? 'produits' : 'products'}</span>
                        </div>
                        
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className={`${
                            isPremium 
                              ? 'text-amber-600 border-amber-400 hover:bg-amber-50' 
                              : 'text-[#0A2540] border-[#0A2540] hover:bg-[#0A2540] hover:text-white'
                          }`}
                        >
                          {i18n.language === 'fr' ? 'Voir la boutique' : 'View shop'}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
